import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    NameValue,
    PersonService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FormatPersonPipe } from '@flyfreely-portal-ui/ui';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from 'libs/ng-select/src/lib/helpers';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Injectable()
export class TableOrganisationPersonnelSelectionService {
    private searchSource = new Subject<NgSelectSearch>();
    results$ = new ReplaySubject<NameValue<number>[]>(1);
    loading$ = new BehaviorSubject<boolean>(false);
    public organisationId: number;

    private items: NameValue<number>[] = [];

    private selectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: 0,
        searchValue: null,
        totalItems: null
    };

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private personService: PersonService,
        private formatPersonPipe: FormatPersonPipe,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.loading$.next(working));

        this.searchSource
            .asObservable()
            .pipe(
                distinctUntilChanged(),
                debounceTime(500),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(search => {
                this.selectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.selectConfig
                );
                this.refreshItems();
            });
    }

    ngOnDestroy() {
        this.results$.complete();
        this.loading$.complete();
        this.searchSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private findOriginalPerson(id: number) {
        this.personService
            .findPerson(id, this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: person => {
                    this.items = this.items.concat([
                        {
                            name: this.formatPersonPipe.transform(person),
                            value: person.id
                        }
                    ]);
                    this.results$.next(this.items);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while loading person: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    refreshItems(initialValue?: number) {
        this.personService
            .findPersonnelGraphQL(
                this.organisationId,
                this.selectConfig.currentPage,
                15,
                {
                    organisationId: this.organisationId,
                    textSearch:
                        this.selectConfig.searchValue?.length > 0
                            ? this.selectConfig.searchValue
                            : null
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: results => {
                    /*  const orgs = results.results.filter(
                        o =>
                            this.items.findIndex((i: any) => i.id === o.id) ===
                            -1
                    );
                    this.items = this.items.concat(orgs);
                    this.selectConfig = updateNgSelectData(
                        results,
                        this.selectConfig
                    );
                    this.resultsSource.next(this.items);
                    this.loadingSource.next(false); */

                    const personnel = results.results.filter(
                        person => !this.items.some(p => p.value === person.id)
                    );
                    this.items = this.items.concat(
                        personnel.map(person => ({
                            name: this.formatPersonPipe.transform(person),
                            value: person.id
                        }))
                    );
                    this.selectConfig = updateNgSelectData(
                        {
                            ...results,
                            pagination: {
                                page: this.selectConfig.currentPage,
                                pageSize: 15
                            }
                        },
                        this.selectConfig
                    );
                    this.results$.next(this.items);
                    if (
                        initialValue != null &&
                        this.items.find(
                            person => person.value === initialValue
                        ) == null
                    ) {
                        this.findOriginalPerson(initialValue);
                    }
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while loading personnel: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    onSelectScroll() {
        const resultLength =
            this.selectConfig.searchValue == null
                ? this.items.length
                : this.items.filter(
                      (i: any) =>
                          i.name
                              .toUpperCase()
                              .indexOf(
                                  this.selectConfig.searchValue.toUpperCase()
                              ) > -1
                  ).length;
        if (
            this.selectConfig.totalItems == null ||
            resultLength !== this.selectConfig.totalItems
        ) {
            this.refreshItems();
        }
    }

    onSelectSearch(search: NgSelectSearch) {
        this.searchSource.next(search);
    }

    onSelectClose() {
        this.selectConfig.searchValue = null;
        this.selectConfig.currentPage = 0;
    }
}
